"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C68 · What a person's generated face is drawn from.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **IT LIVES IN `shared` BECAUSE TWO SIDES MUST AGREE ON THE BYTES, NOT ON THE IDEA.** The
 * API sends this seed beside every avatar URL; the sign-in screen computes it from the address
 * somebody just typed, with no server involved. Those two strings have to be identical or the face
 * on the password step is not the face inside the app — which is the one promise the whole design
 * rests on.
 *
 * ⚠️⚠️ **SO THERE IS ONE IMPLEMENTATION AND IT IS THIS ONE.** The obvious shortcut is
 * `lower(trim(email))` written into each of the fourteen queries that select an avatar; that is
 * the rule copied fourteen times, in a second language, where nothing checks it against the
 * client's. The services select the raw address and map it through this instead.
 *
 * ⚠️ **The face seeds on the EMAIL while the hue seeds on the member id**, and the split is
 * deliberate: an id is stable for life but unknowable before sign-in, and asking the server
 * whether an address exists is the account-enumeration oracle `sign-in.tsx` is built to deny.
 * Seeding on the address makes the login's face correct by construction, and it renders for every
 * syntactically valid address whether or not an account is behind it — a face that appeared only
 * for real accounts would be that oracle again, drawn in pictures.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.avatarSeed = avatarSeed;
exports.wireAvatarSeed = wireAvatarSeed;
/**
 * The seed for somebody's blobatar.
 *
 * ⚠️⚠️ **LOWER-CASED AND TRIMMED, AND NOTHING ELSE.** No dot-stripping, no plus-address folding:
 * those are one provider's delivery policy rather than facts about the address, and folding them
 * would make `g.takacs@…` and `gtakacs@…` the same face for two people who may be two people.
 * Case and surrounding space are the only differences that are never meaningful.
 *
 * ⚠️ **`citext` already makes the COLUMN case-insensitive**, which is why this is easy to forget:
 * Postgres compares `Gabor@…` and `gabor@…` as equal while storing whichever was typed. The bytes
 * still differ, and a hash does not care what Postgres thinks. Normalising here is what makes the
 * stored value and the typed value produce one face.
 *
 * ⚠️ **An empty address seeds an empty string rather than throwing.** The sign-in card draws a face
 * as the address is typed, and a seed that refused an incomplete one would flicker letter by
 * letter.
 */
function avatarSeed(email) {
    return (email ?? '').trim().toLowerCase();
}
/**
 * The same seed, shaped the way it travels on the wire: **`null` where there is nobody.**
 *
 * ⚠️⚠️ **EVERY `<who>AvatarSeed` FIELD IS NULLABLE EXACTLY LIKE THE `<who>AvatarUrl` BESIDE IT**,
 * and that is the whole reason this exists. A project can have no PM and a task no assignee; those
 * rows carry `pmName: null` and the screen draws nothing. `avatarSeed(null)` returns `''` — a
 * perfectly valid seed, which hashes to a perfectly good face — so a mapper that reached for it
 * directly would put *the empty string's face* on every unassigned task in the list, identical on
 * all of them, and it would look deliberate.
 *
 * ⚠️ **So the API maps through this one and the sign-in screen through `avatarSeed`**, and the
 * difference is not stylistic: the login is drawing a face for an address somebody is still typing,
 * where a half-typed seed is the correct answer and `null` would flicker the face out of existence
 * between keystrokes.
 */
function wireAvatarSeed(email) {
    const seed = avatarSeed(email);
    return seed === '' ? null : seed;
}
//# sourceMappingURL=avatar-seed.js.map